import { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faTrash,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { trackButtonClick } from '../../services/telemetry';

interface ProjectMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onRename: () => void;
  onDelete: (e: React.MouseEvent) => void;
  isLoading?: boolean;
}

// Dropdown menu for project actions in project list item, contains rename and delete options
export const ProjectMenu = ({
  isOpen,
  onToggle,
  onRename,
  onDelete,
  isLoading = false,
}: ProjectMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        if (isOpen) {
          onToggle();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onToggle, isOpen]);

  const handleToggleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackButtonClick('Workspace - ProjectMenu', 'Toggle Menu');
    onToggle();
  };

  const handleMenuItemClick =
    (handler: (e: React.MouseEvent) => void, action: string) =>
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      trackButtonClick('Workspace - ProjectMenu', action);
      handler(e);
    };

  return (
    <div className="relative ml-auto" ref={menuRef}>
      <button
        onClick={handleToggleClick}
        className={`
          focus:ring-opacity-50 focus:ring-2 focus:ring-yellow-500
          focus:outline-none
          flex h-8 min-h-[44px] w-8 min-w-[44px] cursor-pointer items-center
          justify-center rounded-full text-gray-400 transition-transform
          duration-100
          hover:bg-gray-200
          active:scale-95 active:bg-gray-300
        `}
        aria-label="Project menu"
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} className="text-gray-600" />
      </button>

      {isOpen && (
        <div
          className={`
            ring-opacity-5 absolute right-0 z-10 mt-2 w-48 origin-top-right
            rounded-md bg-white py-1 shadow-lg ring-1 ring-black
            focus:outline-none
          `}
          role="menu"
          aria-orientation="vertical"
        >
          <button
            onClick={handleMenuItemClick(() => onRename(), 'Rename Project')}
            className={`
              flex min-h-[44px] w-full cursor-pointer items-center px-4 py-2
              text-sm text-gray-700 transition-transform duration-100
              hover:bg-gray-100
              focus:bg-gray-100 focus:outline-none
              active:scale-95 active:bg-gray-200
            `}
            disabled={isLoading}
            role="menuitem"
          >
            <FontAwesomeIcon
              icon={faPen}
              className="mr-2 h-4 w-4 text-gray-500"
            />
            Rename
          </button>
          <button
            onClick={handleMenuItemClick(onDelete, 'Delete Project')}
            className={`
              flex min-h-[44px] w-full cursor-pointer items-center px-4 py-2
              text-sm text-red-600 transition-transform duration-100
              hover:bg-gray-100
              focus:bg-gray-100 focus:outline-none
              active:scale-95 active:bg-gray-200
            `}
            role="menuitem"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2 h-4 w-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
};
